// Imports => React
import {
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { NavLink, useHistory } from "react-router-dom";
import { withStore } from "@stores";
import { observer } from "mobx-react";
import clsx from "clsx";

// Imports => Constants
import { DEFAULT_ROUTE, NAVIGATION_ROUTES, ROUTES } from "@constants";
import { ICONS, KEYS, THEMES } from "@opp/ui-kit";

// Imports => Utilities
import {
  AcIsSet,
  AcIsObject,
  AcIsFunction,
  AcIndicator,
  AcAfterTransitionEnd,
} from "@opp/ui-kit";

// Imports => Hooks
import { usePermissions } from "@hooks";

// Imports => Atoms
import { AcLogo, AcIcon, AcRipple } from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import { useGetSettingsQuery } from "@src/api/openapi/merchantApi";

const _CLASSES = {
  MAIN: "ac-navigation-wrp",
  VISIBLE: "ac-navigation-wrp--visible",
  INDICATOR: {
    MAIN: "ac-navigation__indicator",
    STATIC: "ac-navigation__indicator--static",
    LEFT: "ac-navigation__indicator--move-left",
    RIGHT: "ac-navigation__indicator--move-right",
  },
  NAVIGATION: {
    MAIN: "ac-navigation",
    SUB_LIST: "ac-navigation__list ac-navigation__list--sub",
    TOGGLE: "ac-navigation__toggle",
    LIST: "ac-navigation__list",
    ITEM: "ac-navigation__item",
    PARENT: "ac-navigation__item--parent",
    ACTIVE_PARENT: "ac-navigation__item--parent-active",
    OPEN_PARENT: "ac-navigation__item--parent-open",
    LINK: "ac-navigation__link",
    ACTIVE: "ac-navigation__link--active",
    ICON: "ac-navigation__icon",
    INSET_ICON: "ac-navigation__inset-icon",
    CHILDREN: {
      MAIN: "ac-navigation-children",
      LIST: "ac-navigation-children__list",
      ITEM: "ac-navigation-children__item",
      LINK: "ac-navigation-children__link",
      ACTIVE: "ac-navigation-children__link--active",
      ICON: "ac-navigation-children__icon",
    },
  },
};

let nav_delay = null;
let toggle_delay = null;
let resize_timer = null;
let indicator = null;
let unsubscribeFromStore = null;

const AcNavigation = ({
  store: { auth, profile, ui, settings },
  routes = NAVIGATION_ROUTES,
  selectModalCallback,
  innerRef,
  hasProfiles,
  t,
}) => {
  const history = useHistory();
  const { can, cannot } = usePermissions();
  const { current_roles } = profile;
  const { has_purchases, configurations } = settings;

  const [raised, setRaised] = useState(false);
  const $orientation = useRef(null);
  const $navigation = useRef(null);
  const $indicator = useRef(null);
  const $indicatorInstance = useRef(null);

  const initIndicator = () => {
    if (resize_timer) clearTimeout(resize_timer);

    resize_timer = setTimeout(() => {
      const curOrientation =
        $orientation.current || window.outerWidth >= 992
          ? "vertical"
          : "horizontal";

      if (!AcIsSet($indicatorInstance.current)) {
        $indicatorInstance.current = new AcIndicator(
          $navigation,
          $indicator,
          routes,
          _CLASSES.INDICATOR,
          curOrientation === "vertical"
        );
        if (
          $indicatorInstance.current &&
          AcIsFunction($indicatorInstance.current.init)
        ) {
          $indicatorInstance.current.init().then(init);
        }
      } else if (
        AcIsSet($indicatorInstance.current) &&
        AcIsSet($indicatorInstance.current.update)
      ) {
        $indicatorInstance.current.update(routes, curOrientation).then(init);
      }
    }, 300);
  };

  const handleScrollDirectionChanged = (event) => {
    const {
      detail: { direction },
    } = event;

    const $offcanvas = document.querySelector(
      '[id="ac-navigation--off-canvas"]'
    );

    if ($orientation.current !== "horizontal") return;
    if (!AcIsSet($offcanvas) || !AcIsSet($offcanvas.setAttribute)) return;

    if (direction === "up") {
      $offcanvas.setAttribute("data-raised", false);
    } else {
      $offcanvas.setAttribute("data-raised", true);
    }
  };

  const handleResizeEvent = () => {
    const orientation = window.outerWidth >= 992 ? "vertical" : "horizontal";

    if (orientation !== $orientation.current) {
      initIndicator();
      $orientation.current = orientation;
    }

    if (
      !("ontouchstart" in document.documentElement && window.outerWidth < 768)
    ) {
      const recalculateIndicatorEvent = new CustomEvent("recalculateIndicator");
      window.dispatchEvent(recalculateIndicatorEvent);
    }
  };

  const removeScrollEvent = () => {
    return new Promise((resolve) => {
      window.removeEventListener(
        "scrollDirectionChanged",
        handleScrollDirectionChanged,
        { passive: true }
      );
      resolve();
    });
  };

  const addScrollEvent = () => {
    removeScrollEvent().then(() => {
      window.addEventListener(
        "scrollDirectionChanged",
        handleScrollDirectionChanged,
        { passive: true }
      );
    });
  };

  const removeResizeEvent = () => {
    return new Promise((resolve) => {
      window.removeEventListener("resize", handleResizeEvent, {
        passive: true,
      });
      resolve();
    });
  };

  const addResizeEvent = () => {
    removeResizeEvent().then(() => {
      window.addEventListener("resize", handleResizeEvent, { passive: true });
    });
  };

  useEffect(() => {
    unsubscribeFromStore = history.listen((location, action) => {
      handleSubscribe(location, action);
    });

    initIndicator();
    addResizeEvent();
    addScrollEvent();

    return () => {
      removeResizeEvent();
      if (unsubscribeFromStore) unsubscribeFromStore();
      if (
        AcIsSet($indicatorInstance.instance) &&
        AcIsSet($indicatorInstance.instance.unload)
      ) {
        $indicatorInstance.instance.unload().then(() => {
          $indicatorInstance.instance = null;
        });
      }
    };
  }, [
    $indicator,
    $indicatorInstance,
    $navigation,
    $orientation,
    routes,
    history,
    has_purchases,
    hasProfiles,
  ]);

  const getParent = (location, item) => {
    const { parent, children } = item;
    const collection = children;
    const len = collection.length;
    let n = 0;
    let result = null;

    if (location.pathname.indexOf(parent.path) === -1) return result;

    for (n; n < len; n++) {
      const item = collection[n];
      const { path } = item;

      if (location.pathname.indexOf(path) > -1) {
        result = parent;
        break;
      }
    }

    return result;
  };

  const handleSubscribe = (location, action) => {
    let found = false;
    let n = 0;
    let len = routes.length;

    for (n; n < len; n++) {
      const item = routes[n];
      let active = false;

      if (!AcIsSet(item)) continue;

      const { parent, children } = item;

      if (!AcIsSet(parent) || !AcIsSet(children)) {
        active =
          item.path === location.pathname ||
          location.pathname.indexOf(item.path) > -1;
      } else if (AcIsSet(parent) && AcIsSet(children)) {
        active = getParent(location, item);
      }

      if (active) {
        found = AcIsObject(active) ? active : item;
        handleClickEvent({}, found);
        break;
      }
    }

    if (
      !found &&
      $indicatorInstance &&
      $indicatorInstance.current &&
      $indicatorInstance.current.reset
    ) {
      $indicatorInstance.current.reset();
    } else if (
      found &&
      $indicatorInstance &&
      $indicatorInstance.current &&
      $indicatorInstance.current.recalculate
    ) {
      $indicatorInstance.current.recalculate({}, found);
    }
  };

  const init = () => {
    const { location } = history;
    const len = routes.length;
    let n = 0;

    for (n; n < len; n++) {
      const route = routes[n];
      let active = false;

      if (!AcIsSet(route)) continue;

      const { parent, children } = route;

      if (AcIsSet(parent) && AcIsSet(children)) {
        active = getParent(location, route);
      } else if (route.path !== "/") {
        active =
          route.path === location.pathname ||
          location.pathname.indexOf(route.path) > -1;
      } else {
        active = route.path === location.pathname;
      }

      if (active) {
        const found = AcIsObject(active) ? active.$ref : route.$ref;
        if (found && found.click) found.click();
        break;
      }
    }

    if (
      !("ontouchstart" in document.documentElement && window.outerWidth < 768)
    ) {
      const recalculateIndicatorEvent = new CustomEvent("recalculateIndicator");
      window.dispatchEvent(recalculateIndicatorEvent);
    }

    if ($navigation && $navigation.current) {
    }
    AcAfterTransitionEnd($navigation.current, init);
  };

  const handleClickEvent = (event, route) => {
    if (route.callback) route.callback(event, route);
    else if (route && route.$ref) route.$ref.click();
  };

  const handleToggle = useCallback(
    async (event) => {
      if (toggle_delay) clearTimeout(toggle_delay);
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();

      const state = ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE];
      await ui.setValue(KEYS.NAVIGATION, KEYS.VISIBLE, !state);
      if (!state) await ui.setValue(KEYS.NAVIGATION, KEYS.EXPANDED, []);
    },
    [
      ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE],
      ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED],
    ]
  );

  const handleHide = useCallback(
    async (event) => {
      if (toggle_delay) clearTimeout(toggle_delay);
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();

      await ui.setValue(KEYS.NAVIGATION, KEYS.VISIBLE, false);
      await ui.setValue(KEYS.NAVIGATION, KEYS.EXPANDED, []);
    },
    [
      ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE],
      ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED],
    ]
  );

  useImperativeHandle(innerRef, () => ({
    toggle: handleToggle,
    hide: handleHide,
  }));

  const handleMouseOver = useCallback(
    (event) => {
      if (toggle_delay) clearTimeout(toggle_delay);
      if (nav_delay) clearTimeout(nav_delay);

      if (!ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE]) {
        nav_delay = setTimeout(async () => {
          if (event && event.preventDefault) event.preventDefault();
          if (event && event.stopPropagation) event.stopPropagation();
          await ui.setValue(KEYS.NAVIGATION, KEYS.VISIBLE, true);
        }, 600);
      }
    },
    [
      ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE],
      ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED],
    ]
  );

  const handleMouseLeave = useCallback(
    (event) => {
      if (nav_delay) clearTimeout(nav_delay);
    },
    [ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE]]
  );

  const handleToggleParent = async (event, parent) => {
    if (toggle_delay) clearTimeout(toggle_delay);

    let state = (ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED] || []).slice();
    const index = state.indexOf(parent.id);

    const { location } = history;
    const active =
      location.pathname && location.pathname.indexOf(parent.path) > -1;

    if (active) return;

    if (state && index === -1) {
      state = [parent.id];
      await ui.setValue(KEYS.NAVIGATION, KEYS.EXPANDED, state);
      if (!ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE]) {
        ui.setValue(KEYS.NAVIGATION, KEYS.VISIBLE, true);
      }
    } else if (state && index > -1) {
      state = [];
      await ui.setValue(KEYS.NAVIGATION, KEYS.EXPANDED, state);
    }
  };

  const handleLogoCallback = useCallback(() => {
    const { push } = history;
    if (push) push(DEFAULT_ROUTE.path);
  });

  const getChildrenIconClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.CHILDREN.ICON);
  });

  const getChildrenActiveLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.CHILDREN.ACTIVE);
  });

  const getChildrenLinkClassNames = (path) => {
    const { location } = history;
    const active = location.pathname && location.pathname.indexOf(path) > -1;
    return clsx(
      _CLASSES.NAVIGATION.CHILDREN.LINK,
      active && _CLASSES.NAVIGATION.CHILDREN.ACTIVE
    );
  };

  const getChildrenItemClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.CHILDREN.ITEM);
  });

  const getChildrenListClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.CHILDREN.LIST);
  });

  const getChildrenClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.CHILDREN.MAIN);
  });

  const getInsetIconClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.INSET_ICON);
  });

  const getIconClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.ICON);
  });

  const getActiveLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.ACTIVE);
  });

  const getLinkClassNames = (item) => {
    return clsx(_CLASSES.NAVIGATION.LINK);
  };

  const getParentClassNames = useCallback(
    (path, id) => {
      const { location } = history;
      const active = location.pathname && location.pathname.indexOf(path) > -1;
      const open = ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED].indexOf(id) > -1;
      return clsx(
        _CLASSES.NAVIGATION.ITEM,
        _CLASSES.NAVIGATION.PARENT,
        open && _CLASSES.NAVIGATION.OPEN_PARENT,
        active && _CLASSES.NAVIGATION.ACTIVE_PARENT
      );
    },
    [ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED], history.location]
  );

  const getItemClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.ITEM);
  });

  const getListClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.LIST);
  });

  const getIndicatorClassNames = useMemo(() => {
    return clsx(_CLASSES.INDICATOR.MAIN);
  });

  const getNavigationClassNames = useMemo(() => {
    return clsx(_CLASSES.NAVIGATION.MAIN);
  });

  const getMainClassNames = useMemo(
    () => {
      return clsx(
        _CLASSES.MAIN,
        ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE] && _CLASSES.VISIBLE
      );
    },
    [ui[KEYS.CURRENT_NAVIGATION][KEYS.VISIBLE]],
    raised
  );

  const isActive = (match, location, path) => {
    let result = false;

    if (AcIsSet(match)) {
      result = match.url === location.pathname;
    } else if (location.pathname === "/") {
      result = path === ROUTES.DASHBOARD.path;
    } else {
      result = location.pathname.indexOf(path) > -1;
    }

    return result;
  };

  const renderChildren = (collection, parent) => {
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      if (can(item.allowed)) {
        const { id, label, icon, path } = item;

        const object = (
          <li
            key={`ac-navigation-item-${id}`}
            className={getChildrenItemClassNames}
            role={"presentation"}
            itemProp={"name"}
          >
            <NavLink
              to={path}
              className={getChildrenLinkClassNames(path)}
              activeClassName={getChildrenActiveLinkClassNames}
              title={`${parent} | ${label}`}
              isActive={isActive}
              itemProp={"url"}
              role={"menuitem"}
            >
              {icon && (
                <AcIcon icon={icon} className={getChildrenIconClassNames} />
              )}
              {label}
              <AcRipple theme={THEMES.PITCH} simple />
            </NavLink>
          </li>
        );

        result.push(object);
      }
    }

    return result.length > 0 ? (
      <div className={getChildrenClassNames}>
        <ul
          className={getChildrenListClassNames}
          role={"menubar"}
          itemScope
          itemType={"http://www.schema.org/SiteNavigationElement"}
        >
          {result}
        </ul>
      </div>
    ) : null;
  };

  const renderParent = (item, children) => {
    const { id, label, path, icon } = item;

    const object = (
      <li
        key={`ac-navigation-item-${id}`}
        className={getParentClassNames(path, id)}
        role={"presentation"}
        itemProp={"name"}
      >
        <div
          className={getLinkClassNames(path)}
          title={label}
          onClick={(event) => handleToggleParent(event, item)}
          ref={(node) => {
            //AcAfterTransitionEnd(node, init);
            return (item.$ref = node);
          }}
        >
          {icon && <AcIcon icon={icon} className={getIconClassNames} />}
          {label}
          <AcRipple theme={THEMES.PITCH} simple />
        </div>

        {children && renderChildren(children, label)}
      </li>
    );

    return object;
  };

  const renderNavigationItem = (item) => {
    const { id, name, label, icon, path } = item;

    const object = (
      <li
        key={`ac-navigation-item-${id}`}
        className={getItemClassNames}
        role={"presentation"}
        itemProp={"name"}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        ref={(node) => (item.$ref = node)}
      >
        <NavLink
          to={path}
          className={getLinkClassNames(item)}
          activeClassName={getActiveLinkClassNames}
          title={t(label, { ns: "titles" })}
          isActive={(match, location) => isActive(match, location, path)}
          itemProp={"url"}
          role={"menuitem"}
          exact
          strict
        >
          {icon && <AcIcon icon={icon} className={getIconClassNames} />}
          <em>{t(label, { ns: "titles" })}</em>
          <AcRipple theme={THEMES.PITCH} simple />
        </NavLink>
      </li>
    );

    return object;
  };

  const renderNavigation = useMemo(() => {
    let collection = routes;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      if (!AcIsSet(item)) continue;

      if (item.path === "/payment-links") continue;
      if (!has_purchases && item.path === "/purchases") {
        continue;
      }
      // if there are gonna be more modules, we'll need to check for all of them 
      // and hide if we don't have available modules
      if (
        item.path === "/modules" &&
          (!settings.current_settings?.permissions["payment-links"] ||
          !configurations["payment-links"])
      ) {
        continue;
      }

      const { parent, children } = item;

      let object;

      if (AcIsSet(parent) && AcIsSet(children)) {
        if (can(parent.allowed)) object = renderParent(parent, children);
      } else if (!AcIsSet(parent) || !AcIsSet(children)) {
        if (can(item.allowed)) {
          if (AcIsSet(item.not) && can(item.not)) {
            continue;
          } else {
            object = renderNavigationItem(item);
          }
        }
      }

      if (object) result.push(object);
    }

    if (window.outerWidth < 992 && hasProfiles) {
      result.push(
        <li
          key={`ac-navigation-item-meruid-select`}
          className={`${getItemClassNames} h-hidden-md h-hidden-lg h-hidden-xl`}
          role={"presentation"}
          itemProp={"name"}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className={getLinkClassNames(null)}
            itemProp={"url"}
            role={"menuitem"}
            onClick={(event) => selectModalCallback(event)}
          >
            <AcIcon icon={ICONS.USERS_OUTLINE} className={getIconClassNames} />
            <AcIcon icon={ICONS.SYNC_ALT} className={getInsetIconClassNames} />
            <em>
              {t("switch-merchant-profile", "Switch merchant profile", {
                ns: "common",
              })}
            </em>
            <AcRipple theme={THEMES.PITCH} simple />
          </button>
        </li>
      );
    }

    return result;
  }, [
    current_roles,
    ui[KEYS.CURRENT_NAVIGATION][KEYS.EXPANDED],
    history,
    selectModalCallback,
    has_purchases,
  ]);

  return (
    <div className={getMainClassNames} ref={$navigation} role={"navigation"}>
      <nav className={getNavigationClassNames}>
        <ul
          className={getListClassNames}
          role={"menubar"}
          itemScope
          itemType={"http://www.schema.org/SiteNavigationElement"}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
        >
          {renderNavigation}
        </ul>

        <span ref={$indicator} className={getIndicatorClassNames} />
      </nav>
    </div>
  );
};

export default withTranslation(["titles", "common"])(
  withStore(observer(AcNavigation))
);
